import cx from "classnames";
import { type ReactNode } from "react";

/**
 * Canonical height for EVERY Ask Puntt pane header.
 *
 * Every right-side pane (chat collapse strip, canvas, project status,
 * project-loading skeleton, ticket, asset viewer, artwork / context
 * columns) must render its title bar at this exact height so the column
 * titles line up at the same vertical y-coordinate no matter which
 * pane(s) the route happens to render. Import this constant instead of
 * hard-coding `h-12` so the contract lives in one place.
 */
export const PANE_HEADER_HEIGHT_CLASS = "h-12";

export type PaneHeaderProps = {
  /** Region slug for `data-testid="pane-header-<region>"`. */
  region: string;
  title: ReactNode;
  /** Leading control (back, close, etc.). */
  leading?: ReactNode;
  /** Trailing controls (toolbar, collapse, etc.). */
  trailing?: ReactNode;
  className?: string;
};

/**
 * Shared column header for AskPuntt pane surfaces (artwork, context, ticket).
 * Standardizes height and testids across chat, project workspace, and ticket views.
 */
export function PaneHeader({
  region,
  title,
  leading,
  trailing,
  className,
}: PaneHeaderProps) {
  return (
    <div
      className={cx(
        PANE_HEADER_HEIGHT_CLASS,
        "flex min-w-0 shrink-0 items-center gap-2 border-b border-puntt-neutral-gray-5 px-4",
        className,
      )}
      data-testid={`pane-header-${region}`}
    >
      {leading}
      <h3 className="min-w-0 shrink-0 text-[11px] font-bold uppercase tracking-wide text-puntt-neutral-gray-11">
        {title}
      </h3>
      <div
        className="ml-auto flex shrink-0 items-center gap-2"
        data-testid={`pane-header-${region}-controls`}
      >
        {trailing}
      </div>
    </div>
  );
}
